import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutDashboard, Users, Wrench, FileText, UserCog, LogOut, Building2, CalendarDays, BookOpen, ClipboardList, CreditCard, Sparkles } from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/jobs", label: "Jobs", icon: Wrench, testId: "nav-jobs" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testId: "nav-calendar" },
  { to: "/customers", label: "Customers", icon: Users, testId: "nav-customers" },
  { to: "/estimates", label: "Estimates", icon: ClipboardList, testId: "nav-estimates" },
  { to: "/invoices", label: "Invoices", icon: FileText, testId: "nav-invoices" },
  { to: "/price-book", label: "Price Book", icon: BookOpen, testId: "nav-price-book" },
  { to: "/team", label: "Team", icon: UserCog, testId: "nav-team" },
  { to: "/settings/payments", label: "Payments", icon: CreditCard, testId: "nav-payments" },
  { to: "/settings/billing", label: "Billing", icon: Sparkles, testId: "nav-billing" },
];

// Mobile bottom nav — top 5 most-used
const MOBILE_NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/jobs", label: "Jobs", icon: Wrench, testId: "nav-jobs" },
  { to: "/calendar", label: "Sched.", icon: CalendarDays, testId: "nav-calendar" },
  { to: "/invoices", label: "Invoices", icon: FileText, testId: "nav-invoices" },
  { to: "/customers", label: "More", icon: Users, testId: "nav-customers" },
];

export default function Layout() {
  const { user, company, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 bg-slate-900 text-white">
        <div className="px-6 py-6 border-b border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-blue-600 flex items-center justify-center">
              <Wrench className="w-5 h-5" />
            </div>
            <div>
              <div className="font-bold text-lg" style={{ fontFamily: "Outfit" }}>PlumberHQ</div>
            </div>
          </div>
          {company && (
            <div className="mt-4 flex items-center gap-2 text-xs text-slate-400">
              <Building2 className="w-3.5 h-3.5" />
              <span data-testid="company-name" className="truncate">{company.name}</span>
            </div>
          )}
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              data-testid={testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold transition-colors ${
                  isActive ? "bg-blue-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              <Icon className="w-5 h-5" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-slate-800">
          <div className="px-3 py-2 text-xs text-slate-400 truncate" data-testid="user-name">
            {user?.full_name} • {user?.role}
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-semibold text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LogOut className="w-5 h-5" />
            Log out
          </button>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center">
            <Wrench className="w-4 h-4 text-white" />
          </div>
          <div className="font-bold text-base" style={{ fontFamily: "Outfit" }}>PlumberHQ</div>
        </div>
        <button
          data-testid="logout-btn-mobile"
          onClick={handleLogout}
          className="text-sm font-semibold text-slate-600 px-3 py-1.5 rounded-lg hover:bg-slate-100"
        >
          <LogOut className="w-5 h-5" />
        </button>
      </header>

      {/* Main */}
      <main className="md:pl-64">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-safe">
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 h-16 grid grid-cols-5 pb-[env(safe-area-inset-bottom)]">
        {MOBILE_NAV.map(({ to, label, icon: Icon, testId }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            data-testid={`${testId}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold ${
                isActive ? "text-blue-600" : "text-slate-500"
              }`
            }
          >
            <Icon className="w-5 h-5" />
            {label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
